import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Bell, Check, CheckCircle, Warning, Info } from '@phosphor-icons/react';

const ALERT_ICONS = {
  budget_exceeded: Warning,
  bill_due: Bell,
  stock_alert: Info,
  custom: Bell,
};

const ALERT_COLORS = {
  budget_exceeded: { bg: 'bg-[#A63C24]/10', text: 'text-[#A63C24]' },
  bill_due: { bg: 'bg-[#D98324]/10', text: 'text-[#D98324]' },
  stock_alert: { bg: 'bg-[#1E3F2D]/10', text: 'text-[#1E3F2D]' },
  custom: { bg: 'bg-[#5C5A56]/10', text: 'text-[#5C5A56]' },
};

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get('/alerts').then(r => setAlerts(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleMarkRead = async (id) => {
    await api.put(`/alerts/${id}/read`);
    load();
  };

  const handleMarkAllRead = async () => {
    await api.put('/alerts/read-all');
    load();
  };

  const unread = alerts.filter(a => !a.read);
  const read = alerts.filter(a => a.read);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Alerts</h1>
          <p className="text-[#5C5A56] text-sm mt-1">Stay on top of your financial notifications</p>
        </div>
        {unread.length > 0 && (
          <button
            data-testid="mark-all-read-btn"
            onClick={handleMarkAllRead}
            className="bg-[#FBF9F6] text-[#5C5A56] font-semibold rounded-xl px-5 py-2.5 hover:bg-white border border-[#E8E6E1] transition-colors text-sm flex items-center gap-2"
          >
            <CheckCircle size={16} /> Mark all as read
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#1E3F2D] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : alerts.length === 0 ? (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-12 text-center">
          <Bell size={40} weight="duotone" className="text-[#E8E6E1] mx-auto mb-3" />
          <p className="text-[#5C5A56] text-sm">No alerts yet. Alerts will appear when budget limits are exceeded or bills are due.</p>
        </div>
      ) : (
        <>
          {unread.length > 0 && (
            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-3">
                Unread ({unread.length})
              </h3>
              <div className="space-y-3" data-testid="unread-alerts">
                {unread.map(a => {
                  const Icon = ALERT_ICONS[a.type] || Bell;
                  const colors = ALERT_COLORS[a.type] || ALERT_COLORS.custom;
                  return (
                    <div key={a.id} className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 flex items-start gap-3 hover:shadow-md transition-all" data-testid={`alert-${a.id}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
                        <Icon size={18} weight="duotone" className={colors.text} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1C1B1A]">{a.title}</p>
                        <p className="text-sm text-[#5C5A56] mt-0.5">{a.message}</p>
                        <p className="text-xs text-[#5C5A56]/60 mt-1">{new Date(a.created_at).toLocaleString()}</p>
                      </div>
                      <button
                        onClick={() => handleMarkRead(a.id)}
                        className="text-[#5C5A56] hover:text-[#265C42] transition-colors p-1 flex-shrink-0"
                        title="Mark as read"
                        data-testid={`read-${a.id}`}
                      >
                        <Check size={18} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {read.length > 0 && (
            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-3">
                Read ({read.length})
              </h3>
              <div className="space-y-3 opacity-60" data-testid="read-alerts">
                {read.map(a => {
                  const Icon = ALERT_ICONS[a.type] || Bell;
                  return (
                    <div key={a.id} className="bg-white/60 border border-[#E8E6E1] rounded-2xl p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#FBF9F6]">
                        <Icon size={18} weight="regular" className="text-[#5C5A56]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#5C5A56]">{a.title}</p>
                        <p className="text-xs text-[#5C5A56]/80 mt-0.5">{a.message}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
